/* Section « MacBook » pilotée par le scroll (Three.js chargé en différé).
   - Les textes et l'image de repli sont animés par le scroll même sans 3D.
   - Le 3D n'est chargé que lorsque la section approche du viewport.
   - Désactivé sur mobile, écran tactile, connexion lente, appareil peu puissant ou sans WebGL.
   - Modèle de remplacement : data-model (URL d'un .glb ; si le fichier contient une animation, elle est jouée à l'ouverture). */
(function () {
  'use strict';
  var root = document.querySelector('[data-showcase]');
  if (!root) return;

  var THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
  var GLTF_URL = 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/loaders/GLTFLoader.js/+esm';
  var mq = function (q) { return window.matchMedia && window.matchMedia(q).matches; };
  var reduce = mq('(prefers-reduced-motion: reduce)');
  var animated = root.hasAttribute('data-anim') && !reduce;
  var clamp = function (v) { return Math.min(1, Math.max(0, v)); };
  var seg = function (p, a, b) { return clamp((p - a) / (b - a)); };
  var ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
  var mix = function (a, b, t) { return a + (b - a) * t; };
  var stage = root.querySelector('.showcase__stage');
  var scene3d = null, ticking = false;

  /* ---------- Progression du scroll -> variables CSS ---------- */
  function progress() {
    var r = root.getBoundingClientRect();
    return clamp(-r.top / Math.max(1, r.height - window.innerHeight));
  }
  function setVars(p) {
    var s = function (k, v) { root.style.setProperty(k, v.toFixed(3)); };
    s('--intro', 1 - ease(seg(p, 0.12, 0.34)));
    s('--img', ease(seg(p, 0, 0.3)));
    s('--img2', ease(seg(p, 0.5, 0.78)));
    s('--l1', ease(seg(p, 0.76, 0.82)));
    s('--l2', ease(seg(p, 0.8, 0.86)));
    s('--l3', ease(seg(p, 0.84, 0.9)));
    s('--pr', ease(seg(p, 0.88, 0.94)));
    s('--cta', ease(seg(p, 0.92, 0.98)));
  }
  function update() {
    ticking = false;
    var r = root.getBoundingClientRect();
    if (r.bottom < -100 || r.top > window.innerHeight + 100) return;
    var p = progress();
    setVars(p);
    if (scene3d) scene3d.render(p);
  }
  function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }

  if (!animated) { setVars(1); return maybe3D(false); }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
  maybe3D(true);

  /* ---------- Chargement différé : WebGL > MacBook 3D en CSS > photo ---------- */
  function hasWebGL() {
    try {
      var cv = document.createElement('canvas');
      return !!(cv.getContext('webgl2') || cv.getContext('webgl'));
    } catch (e) { return false; }
  }
  function webglAllowed() {
    if (/[?&]nogl\b/.test(location.search)) return false;
    if (mq('(max-width: 820px)') || mq('(pointer: coarse)')) return false;   // mobile : version CSS plus légère
    var c = navigator.connection;
    if (c && (c.saveData || /2g/.test(c.effectiveType || ''))) return false;
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 2) return false;
    return hasWebGL();
  }
  function maybe3D(scrollDriven) {
    if (!scrollDriven || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (en) {
      if (!en[0].isIntersecting) return;
      io.disconnect();
      if (webglAllowed()) import(THREE_URL).then(init3D).catch(css3d);
      else css3d();
    }, { rootMargin: '900px 0px' });
    io.observe(root);
  }

  /* ---------- Scène 3D ---------- */
  function init3D(THREE) {
    var renderer;
    try { renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' }); }
    catch (e) { css3d(); return; }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.VSMShadowMap;
    var canvas = renderer.domElement;
    canvas.className = 'showcase__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    stage.appendChild(canvas);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(26, 1, 1, 500);
    scene.environment = makeEnv(THREE, renderer);

    var key = new THREE.DirectionalLight(0xffffff, 0.8);
    key.position.set(-20, 60, 24);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.radius = 12; key.shadow.blurSamples = 16; key.shadow.bias = -0.0006;
    var sc = key.shadow.camera; sc.left = -34; sc.right = 34; sc.top = 34; sc.bottom = -34; sc.near = 5; sc.far = 150;
    scene.add(key);

    /* sol : tache de lumière très douce + ombres (pas de glow) */
    var ground = new THREE.Mesh(new THREE.PlaneGeometry(260, 260), new THREE.ShadowMaterial({ opacity: 0.55 }));
    ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; scene.add(ground);
    function radial(stops, size) {
      var c = document.createElement('canvas'); c.width = c.height = size || 256;
      var x = c.getContext('2d'), h = c.width / 2, g = x.createRadialGradient(h, h, 4, h, h, h - 2);
      stops.forEach(function (s) { g.addColorStop(s[0], s[1]); });
      x.fillStyle = g; x.fillRect(0, 0, c.width, c.height);
      return new THREE.CanvasTexture(c);
    }
    var pool = new THREE.Mesh(new THREE.PlaneGeometry(110, 80),
      new THREE.MeshBasicMaterial({ map: radial([[0, 'rgba(255,255,255,.11)'], [1, 'rgba(255,255,255,0)']]), transparent: true, depthWrite: false }));
    pool.rotation.x = -Math.PI / 2; pool.position.y = 0.01; scene.add(pool);
    var contact = new THREE.Mesh(new THREE.PlaneGeometry(38, 25),
      new THREE.MeshBasicMaterial({ map: radial([[0, 'rgba(0,0,0,.75)'], [1, 'rgba(0,0,0,0)']], 128), transparent: true, depthWrite: false }));
    contact.rotation.x = -Math.PI / 2; contact.position.y = 0.02; scene.add(contact);

    var rig = new THREE.Group(); scene.add(rig);
    var lidPivot = null, mixer = null, clip = null, glb = false;

    function procedural() {
      var m = buildMacBook(THREE, root.getAttribute('data-screen'), root.hasAttribute('data-logo'));
      lidPivot = m.lid; rig.add(m.group);
    }

    function size() {
      var w = stage.clientWidth, h = stage.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h; camera.updateProjectionMatrix();
      if (scene3d) scene3d.render(progress());
    }

    function done() {
      size();
      scene3d = { render: render };
      render(progress());
      requestAnimationFrame(function () { root.classList.add('is-3d'); });
    }

    var modelUrl = root.getAttribute('data-model');
    if (modelUrl) {
      import(GLTF_URL).then(function (mod) {
        new mod.GLTFLoader().load(modelUrl, function (g) {
          var obj = g.scene, box = new THREE.Box3().setFromObject(obj), sz = box.getSize(new THREE.Vector3());
          obj.scale.setScalar(32 / Math.max(sz.x, sz.y, sz.z));
          box.setFromObject(obj);
          var c = box.getCenter(new THREE.Vector3());
          obj.position.set(-c.x, -box.min.y, -c.z);
          obj.traverse(function (o) { if (o.isMesh) o.castShadow = true; });
          rig.add(obj); glb = true;
          if (g.animations && g.animations.length) {
            mixer = new THREE.AnimationMixer(obj); clip = g.animations[0];
            var act = mixer.clipAction(clip); act.play(); act.paused = true;
          }
          done();
        }, undefined, function () { procedural(); done(); });
      }).catch(function () { procedural(); done(); });
    } else { procedural(); done(); }

    /* ---------- Chorégraphie liée au scroll ---------- */
    function render(p) {
      var arrive = ease(seg(p, 0, 0.25));            // 0-25 % : le MacBook arrive
      var open = ease(seg(p, 0.5, 0.75));            // 50-75 % : il s'ouvre
      var drift = ease(seg(p, 0.25, 0.75));          // légère rotation pendant le scroll

      rig.position.x = mix(40, 0, arrive);
      rig.position.z = mix(-10, 0, arrive);
      rig.rotation.y = mix(-0.8, -0.3, arrive) + mix(0, 0.22, drift);
      if (lidPivot) lidPivot.rotation.x = Math.PI / 2 - (104 * Math.PI / 180) * open;
      if (mixer && clip) mixer.setTime(clip.duration * open);

      var cam = ease(seg(p, 0.15, 0.8));
      camera.position.set(0, mix(34, 15, cam), mix(100, 88, cam));
      camera.lookAt(0, mix(1.5, 6.8, ease(seg(p, 0.4, 0.78))), 0);

      key.position.x = mix(-28, -8, ease(p));        // la lumière évolue subtilement
      key.intensity = mix(0.55, 0.95, open);
      renderer.toneMappingExposure = mix(1.08, 1.1, open);
      renderer.render(scene, camera);
    }

    if ('ResizeObserver' in window) new ResizeObserver(size).observe(stage);
    canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); scene3d = null; root.classList.remove('is-3d'); });
  }

  /* ---------- Repli : MacBook en CSS 3D (aucun WebGL requis) ---------- */
  function wallUrl(src) {
    if (src) return src;
    var c = document.createElement('canvas'); c.width = 960; c.height = 600;
    var x = c.getContext('2d'), g = x.createLinearGradient(0, 0, 960, 600);
    g.addColorStop(0, '#2b5fd6'); g.addColorStop(0.55, '#5b6ee8'); g.addColorStop(1, '#a56be0');
    x.fillStyle = g; x.fillRect(0, 0, 960, 600);
    function wave(y, amp, col, al, ph) {
      x.beginPath(); x.moveTo(0, 600);
      for (var i = 0; i <= 960; i += 16) x.lineTo(i, y + Math.sin(i / 180 + ph) * amp + Math.sin(i / 70 + ph * 2) * amp * 0.25);
      x.lineTo(960, 600); x.closePath(); x.fillStyle = col; x.globalAlpha = al; x.fill(); x.globalAlpha = 1;
    }
    wave(230, 52, '#7fd0ff', 0.35, 0.4); wave(320, 60, '#ff9ad5', 0.3, 1.8); wave(400, 45, '#2a2f9e', 0.45, 3.1); wave(490, 38, '#0c1a5e', 0.55, 4.4);
    try { return c.toDataURL('image/jpeg', 0.82); } catch (e) { return ''; }
  }
  function css3d() {
    if (root.classList.contains('is-3d') || root.classList.contains('is-css3d')) return;
    var logo = root.hasAttribute('data-logo')
      ? '<svg class="mbx__logo" viewBox="0 0 384 512" aria-hidden="true"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>' : '';
    var el = document.createElement('div');
    el.className = 'mbx'; el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<div class="mbx__rig"><div class="mbx__base"><div class="mbx__kb"></div><div class="mbx__tp"></div></div>' +
      '<div class="mbx__lid"><div class="mbx__screen"><div class="mbx__display"><img class="mbx__wall" alt="" src="' + wallUrl(root.getAttribute('data-screen')) + '"><i class="mbx__notch"></i></div></div>' +
      '<div class="mbx__back">' + logo + '</div></div></div>';
    stage.appendChild(el);
    root.classList.add('is-css3d');
    scene3d = {
      render: function (p) {
        var arrive = ease(seg(p, 0, 0.25)), open = ease(seg(p, 0.5, 0.75)), cam = ease(seg(p, 0.08, 0.72));
        var s = function (k, v) { el.style.setProperty(k, v); };
        s('--x', mix(40, 0, arrive).toFixed(2));
        s('--lid', (-90 + 100 * open).toFixed(2) + 'deg');
        s('--tilt', mix(-58, -9, cam).toFixed(2) + 'deg');
        s('--spin', (mix(-40, -10, ease(seg(p, 0, 0.7))) + 6 * ease(seg(p, 0.25, 0.75))).toFixed(2) + 'deg');
        s('--sc', mix(0.82, 1, ease(seg(p, 0, 0.6))).toFixed(3));
        s('--y', mix(-1, 2, ease(seg(p, 0.2, 0.75))).toFixed(2));
        s('--screen', seg(p, 0.55, 0.75).toFixed(3));
      }
    };
    scene3d.render(progress());
  }

  /* ---------- Environnement lumineux (studio doux, sans fichier HDR) ---------- */
  function makeEnv(THREE, renderer) {
    var s = new THREE.Scene();
    var room = new THREE.Mesh(new THREE.BoxGeometry(120, 70, 120), new THREE.MeshBasicMaterial({ color: 0x75757a, side: THREE.BackSide }));
    room.position.y = 25; s.add(room);
    function panel(w, h, x, y, z, rx, ry, v) {
      var m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color().setScalar(v), side: THREE.DoubleSide }));
      m.position.set(x, y, z); m.rotation.set(rx, ry, 0); s.add(m);
    }
    panel(70, 46, 0, 58, 0, Math.PI / 2, 0, 10);          // grand softbox au-dessus
    panel(18, 54, -46, 26, 8, 0, Math.PI / 2, 7);         // bandeau lumineux à gauche
    panel(34, 40, 52, 22, -4, 0, -Math.PI / 2, 3.5);      // fill à droite
    panel(60, 22, 0, 18, -56, 0, 0, 2.2);                 // fond
    var pm = new THREE.PMREMGenerator(renderer);
    var tex = pm.fromScene(s, 0.035).texture;
    pm.dispose();
    return tex;
  }

  /* ---------- MacBook procédural ---------- */
  function rrect(THREE, w, h, r) {
    var s = new THREE.Shape(), x = -w / 2, y = -h / 2;
    s.moveTo(x + r, y); s.lineTo(x + w - r, y);
    s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false); s.lineTo(x + w, y + h - r);
    s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false); s.lineTo(x + r, y + h);
    s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false); s.lineTo(x, y + r);
    s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
    return s;
  }

  function extrude(THREE, w, h, r, depth, bevel) {
    return new THREE.ExtrudeGeometry(rrect(THREE, w - 2 * bevel, h - 2 * bevel, Math.max(0.05, r - bevel)),
      { depth: depth - 2 * bevel, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 4, curveSegments: 24 });
  }

  function wallpaper(THREE, src) {
    if (src) {
      var t = new THREE.TextureLoader();
      t.setCrossOrigin('anonymous');
      var tex = t.load(src);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 8;
      return tex;
    }
    var c = document.createElement('canvas'); c.width = 1280; c.height = 800;
    var x = c.getContext('2d');
    var bg = x.createLinearGradient(0, 0, 1280, 800);
    bg.addColorStop(0, '#2b5fd6'); bg.addColorStop(0.55, '#5b6ee8'); bg.addColorStop(1, '#a56be0');
    x.fillStyle = bg; x.fillRect(0, 0, 1280, 800);
    function wave(y, amp, color, alpha, ph) {
      x.beginPath(); x.moveTo(0, 800);
      for (var i = 0; i <= 1280; i += 20) x.lineTo(i, y + Math.sin(i / 230 + ph) * amp + Math.sin(i / 90 + ph * 2) * amp * 0.25);
      x.lineTo(1280, 800); x.closePath(); x.fillStyle = color; x.globalAlpha = alpha; x.fill(); x.globalAlpha = 1;
    }
    wave(300, 70, '#7fd0ff', 0.35, 0.4); wave(430, 80, '#ff9ad5', 0.30, 1.8);
    wave(540, 60, '#2a2f9e', 0.45, 3.1); wave(650, 50, '#0c1a5e', 0.55, 4.4);
    x.fillStyle = 'rgba(255,255,255,.18)'; x.fillRect(0, 0, 1280, 18);
    var tx = new THREE.CanvasTexture(c);
    tx.colorSpace = THREE.SRGBColorSpace; tx.anisotropy = 8;
    return tx;
  }

  function buildMacBook(THREE, screenSrc, withLogo) {
    var W = 30.4, D = 21.5, T = 1.1, LID = 0.5, H = 21.2, KEY_H = 0.22;
    var group = new THREE.Group();

    var alu = new THREE.MeshPhysicalMaterial({ color: 0xd7d9dc, metalness: 1, roughness: 0.36, envMapIntensity: 1.05 });
    var aluLid = new THREE.MeshPhysicalMaterial({ color: 0xd7d9dc, metalness: 1, roughness: 0.3, envMapIntensity: 1.1 });
    var black = new THREE.MeshStandardMaterial({ color: 0x0b0b0d, roughness: 0.85, metalness: 0 });
    var keyMat = new THREE.MeshStandardMaterial({ color: 0x141417, roughness: 0.55, metalness: 0, envMapIntensity: 0.5 });

    /* châssis */
    var bg = extrude(THREE, W, D, 1.4, T, 0.13);
    bg.rotateX(-Math.PI / 2); bg.translate(0, 0.13, 0);
    var base = new THREE.Mesh(bg, alu); base.castShadow = true; base.receiveShadow = true;
    group.add(base);

    /* clavier : puits + touches instanciées */
    var kbW = 27.4, kbTop = -D / 2 + 1.75, y = T + 0.012;
    var well = new THREE.Mesh(new THREE.ShapeGeometry(rrect(THREE, kbW + 0.7, 11.5, 0.5)), black);
    well.rotation.x = -Math.PI / 2; well.position.set(0, y, kbTop + 11.5 / 2 - 0.35); group.add(well);

    var rep = function (v, n) { var a = []; for (var i = 0; i < n; i++) a.push(v); return a; };
    var rows = [
      { h: 0.95, p: 1.3, k: [1.5].concat(rep(1, 12), [1]) },
      { h: 1.62, p: 1.89, k: rep(1, 13).concat([1.5]) },
      { h: 1.62, p: 1.89, k: [1.5].concat(rep(1, 12), [1]) },
      { h: 1.62, p: 1.89, k: [1.75].concat(rep(1, 11), [1.75]) },
      { h: 1.62, p: 1.89, k: [2.25].concat(rep(1, 10), [2.25]) },
      { h: 1.62, p: 1.89, k: [1.25, 1.25, 1.25, 1.25, 5, 1.25, 1.25, 2] }
    ];
    var count = 0; rows.forEach(function (r) { count += r.k.length; });
    var kg = new THREE.ExtrudeGeometry(rrect(THREE, 0.92, 0.92, 0.14), { depth: 0.14, bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.04, bevelSegments: 2, curveSegments: 6 });
    kg.rotateX(-Math.PI / 2);
    var keys = new THREE.InstancedMesh(kg, keyMat, count);
    var u = kbW / 14.5, m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), pos = new THREE.Vector3(), scl = new THREE.Vector3(), idx = 0, z = kbTop;
    rows.forEach(function (r) {
      var xc = -kbW / 2;
      r.k.forEach(function (w) {
        var kw = w * u - 0.2;
        pos.set(xc + (w * u) / 2, T + 0.02, z + r.p / 2);
        scl.set(kw, 1, r.h);
        m4.compose(pos, q, scl);
        keys.setMatrixAt(idx++, m4);
        xc += w * u;
      });
      z += r.p;
    });
    keys.castShadow = false; group.add(keys);

    /* trackpad */
    var tpZ = D / 2 - 1.4 - 3.15;
    var groove = new THREE.Mesh(new THREE.ShapeGeometry(rrect(THREE, 11.2, 6.5, 0.55)), new THREE.MeshStandardMaterial({ color: 0x8b8e93, metalness: 0.9, roughness: 0.5 }));
    groove.rotation.x = -Math.PI / 2; groove.position.set(0, T + 0.006, tpZ); group.add(groove);
    var tp = new THREE.Mesh(new THREE.ShapeGeometry(rrect(THREE, 11.0, 6.3, 0.5)), new THREE.MeshPhysicalMaterial({ color: 0xdadcdf, metalness: 0.9, roughness: 0.18, envMapIntensity: 0.9 }));
    tp.rotation.x = -Math.PI / 2; tp.position.set(0, T + 0.012, tpZ); group.add(tp);

    /* charnière */
    var hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, W - 9, 24), new THREE.MeshStandardMaterial({ color: 0x2a2b2e, metalness: 0.85, roughness: 0.4 }));
    hinge.rotation.z = Math.PI / 2; hinge.position.set(0, T + 0.05, -D / 2 + 0.4); group.add(hinge);

    /* capot */
    var lid = new THREE.Group();
    lid.position.set(0, T + KEY_H + 0.04, -D / 2 + 0.5);
    var lg = extrude(THREE, W, H, 1.4, LID, 0.12);
    lg.translate(0, H / 2, -LID + 0.12);
    var lidMesh = new THREE.Mesh(lg, aluLid); lidMesh.castShadow = true; lidMesh.receiveShadow = true;
    lid.add(lidMesh);

    /* logo poli sur le capot (dessiné depuis un tracé SVG) */
    if (withLogo) {
      var lc = document.createElement('canvas'); lc.width = lc.height = 512;
      var lx = lc.getContext('2d');
      lx.translate(256, 256); lx.scale(0.8, 0.8); lx.translate(-192, -256);
      lx.fillStyle = '#fff';
      lx.fill(new Path2D("M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"));
      var ltex = new THREE.CanvasTexture(lc); ltex.anisotropy = 8;
      var logo = new THREE.Mesh(new THREE.PlaneGeometry(5.8, 5.8),
        new THREE.MeshPhysicalMaterial({ color: 0x6f7378, metalness: 1, roughness: 0.1, envMapIntensity: 1.0, alphaMap: ltex, transparent: true }));
      logo.rotation.y = Math.PI;
      logo.position.set(0, H / 2, -LID - 0.006);
      lid.add(logo);
    }

    var glassW = W - 0.8, glassH = H - 0.9;
    var glass = new THREE.Mesh(new THREE.ShapeGeometry(rrect(THREE, glassW, glassH, 0.6)),
      new THREE.MeshPhysicalMaterial({ color: 0x050506, roughness: 0.14, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.08, envMapIntensity: 0.7 }));
    glass.position.set(0, H / 2, 0.004); lid.add(glass);

    var dW = 28.6, dH = 17.9, dTop = H - 0.45 - 0.85;
    var display = new THREE.Mesh(new THREE.PlaneGeometry(dW, dH),
      new THREE.MeshBasicMaterial({ map: wallpaper(THREE, screenSrc), toneMapped: false, color: 0xe6e6e6 }));
    display.position.set(0, dTop - dH / 2, 0.008); lid.add(display);

    var notch = new THREE.Mesh(new THREE.ShapeGeometry(rrect(THREE, 5.4, 0.9, 0.3)), new THREE.MeshBasicMaterial({ color: 0x030304 }));
    notch.position.set(0, dTop - 0.42, 0.01); lid.add(notch);

    group.add(lid);
    return { group: group, lid: lid };
  }
})();
